import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@ludolio/shared-ui';
import { FullPageMessage, FullPageSpinner } from '@/components/FullPageMessage';
import { LabsUnavailable } from '@/components/LabsUnavailable';
import { Layout } from '@/components/Layout';
import { RequireAuth } from '@/components/RequireAuth';
import { getErrorMessage, getErrorStatus, labsApi } from '@/lib/api';
import { LabsContextValue } from '@/lib/labs-context';
import type { LabsContext } from '@/lib/types';

export const Route = createFileRoute('/$orgSlug')({
  component: () => (
    <RequireAuth>
      <OrgShell />
    </RequireAuth>
  ),
});

function OrgShell() {
  const { orgSlug } = Route.useParams();
  const [context, setContext] = useState<LabsContext | 'unavailable' | 'loading' | { error: string }>('loading');

  const load = useCallback(() => {
    setContext('loading');
    labsApi
      .context(orgSlug)
      .then(setContext)
      .catch((error) => {
        if (getErrorStatus(error) === 404) setContext('unavailable');
        else setContext({ error: getErrorMessage(error) });
      });
  }, [orgSlug]);

  useEffect(() => {
    load();
  }, [load]);

  if (context === 'loading') return <FullPageSpinner />;
  if (context === 'unavailable') return <LabsUnavailable />;
  if ('error' in context) {
    return (
      <FullPageMessage title={context.error}>
        <Button type="button" onClick={load}>Try again</Button>
      </FullPageMessage>
    );
  }

  return (
    <LabsContextValue.Provider value={context}>
      <Layout>
        <Outlet />
      </Layout>
    </LabsContextValue.Provider>
  );
}
